<template>
  <div v-if="loading" class="trezo-card bg-white dark:bg-[#0c1427] rounded-md">
    <div class="flex justify-center items-center p-8">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500 opacity-75"></div>
    </div>
  </div>

  <div v-else class="trezo-card bg-white dark:bg-[#0c1427] rounded-md">
    <div
      class="h-[120px] rounded-t-md"
      style="background: linear-gradient(101deg, #605dff 0%, #605dff 100%)"
    >
      <!-- Image removed -->
    </div>

    <div
      class="trezo-card-content px-[20px] md:px-[25px] pb-[20px] md:pb-[25px] text-center"
    >
      <img
        :src="assignedUser?.profile_picture_link || placeholderImage"
        class="inline-block rounded-full w-[167px] h-[167px] mb-[16px] -mt-[83px] object-cover"
        alt="user-image"
      />
      <h3 class="text-md mb-[3px]">{{ assignedUser?.display_name || 'Unassigned' }}</h3>
      <span class="block"> Property Representative </span>

      <ul class="mt-5">
        <li v-if="property?.id" class="flex items-center justify-between mb-[11px] last:mb-0">
          <span class="block text-black dark:text-white"> Property ID: </span>
          <span class="block"> {{ shortenId(property.id) }} </span>
        </li>
        <li v-if="property?.year_built" class="flex items-center justify-between mb-[11px] last:mb-0">
          <span class="block text-black dark:text-white"> Year Built: </span>
          <span class="block"> {{ property.year_built }} </span>
        </li>
        <li v-if="property?.property_type" class="flex items-center justify-between mb-[11px] last:mb-0">
          <span class="block text-black dark:text-white"> Property Type: </span>
          <span class="block"> {{ formatPropertyType(property.property_type) }} </span>
        </li>
        <li v-if="property?.created_at" class="flex items-center justify-between mb-[11px] last:mb-0">
          <span class="block text-black dark:text-white"> Listing Date: </span>
          <span class="block"> {{ formatDate(property.created_at) }} </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, computed, PropType } from "vue";
import { useRoute } from "vue-router";
import { supabase } from "@/utils/supabase";
import placeholderImage from '@/assets/images/properties/property-placeholder.jpg';

interface Property {
  id: string;
  year_built?: number;
  property_type?: string;
  created_at: string;
  assigned_to?: string;
}

interface AssignedUser {
  id: string;
  user_id: string;
  display_name: string;
  profile_picture_link?: string;
  email?: string;
}

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Owner",
  setup() {
    const route = useRoute();
    const loading = ref(true);
    const property = ref<Property | null>(null);
    const assignedUser = ref<AssignedUser | null>(null);

    const shortenId = (id: string) => {
      return id.substring(0, 8) + '...';
    };

    const formatPropertyType = (type: string) => {
      if (!type) return 'N/A';
      // Capitalize first letter and replace hyphens with spaces
      return type.charAt(0).toUpperCase() + 
        type.slice(1).replace(/-/g, ' ');
    };

    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }).format(date);
    };

    const loadProperty = async () => {
      try {
        loading.value = true;
        const propertyId = route.query.id as string;
        
        if (!propertyId) {
          loading.value = false;
          return;
        }

        const { data, error } = await supabase
          .from('property')
          .select('id, year_built, property_type, created_at, assigned_to')
          .eq('id', propertyId)
          .single();

        if (error) {
          console.error('Error fetching property:', error);
          loading.value = false;
          return;
        }

        property.value = data;
        
        if (data?.assigned_to) {
          await loadAssignedUser(data.assigned_to);
        } else {
          loading.value = false;
        }
      } catch (err) {
        console.error('Error in loadProperty:', err);
        loading.value = false;
      }
    };

    const loadAssignedUser = async (userId: string) => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, user_id, display_name, profile_picture_link, email')
          .eq('user_id', userId)
          .single();

        if (error) {
          console.error('Error fetching assigned user:', error);
        } else {
          assignedUser.value = data;
        }
      } catch (err) {
        console.error('Error in loadAssignedUser:', err);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadProperty();
    });

    // Watch for changes in route query to reload data
    watch(() => route.query.id, (newId) => {
      if (newId) {
        loadProperty();
      }
    });

    return {
      loading,
      property,
      assignedUser,
      shortenId,
      formatPropertyType,
      formatDate,
      placeholderImage
    };
  }
});
</script>
